import { useEffect, useRef, useState } from 'react';
import type { TextInput as RNTextInput } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import {
  formatMinutes,
  formatSeconds,
} from '@/components/Custom/CustomClock/CustomClock.utils';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import TextInput from '@/components/Unistyles/TextInput';

interface ClockProps {
  displayMinutes?: string;
  displaySeconds?: string;
  onChange?: (minutes: number, seconds: number) => void;
  isDisabled?: boolean;
  isBottomSheet?: boolean;
  color?: string;
  maxSeconds?: number;
  testIDPrefix?: string;
}

const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(value, max));
};

export default function Clock({
  isBottomSheet = false,
  displayMinutes = '0',
  displaySeconds = '00',
  onChange,
  isDisabled = false,
  maxSeconds,
  testIDPrefix,
}: ClockProps) {
  style.useVariants({
    disabled: isDisabled,
  });

  const isInteractive = !!onChange && !isDisabled;
  const [minutes, setMinutes] = useState<string>(() =>
    formatMinutes(displayMinutes),
  );
  const [seconds, setSeconds] = useState<string>(() =>
    formatSeconds(displaySeconds),
  );

  const [editing, setEditing] = useState<'minutes' | 'seconds' | 'none'>(
    'none',
  );

  const minutesInputRef = useRef<RNTextInput | null>(null);
  const secondsInputRef = useRef<RNTextInput | null>(null);

  useEffect(() => {
    if (editing !== 'minutes') {
      setMinutes(formatMinutes(displayMinutes));
    }
  }, [displayMinutes, editing]);

  useEffect(() => {
    if (editing !== 'seconds') {
      setSeconds(formatSeconds(displaySeconds));
    }
  }, [displaySeconds, editing]);

  const handleFocus = (part: 'minutes' | 'seconds') => {
    if (!isInteractive) return;
    setEditing(part);

    setTimeout(() => {
      if (part === 'minutes' && minutesInputRef.current) {
        if (typeof minutesInputRef.current?.setSelection === 'function') {
          minutesInputRef.current.setSelection(0, minutes.length || 1);
        }
      } else if (part === 'seconds' && secondsInputRef.current) {
        if (typeof secondsInputRef.current?.setSelection === 'function') {
          secondsInputRef.current.setSelection(0, seconds.length || 1);
        }
      }
    }, 0);
  };

  const commitClockInput = () => {
    const inputMinutes = Number.parseInt(minutes, 10) || 0;
    const inputSeconds = Number.parseInt(seconds, 10) || 0;

    const totalInputSeconds = inputMinutes * 60 + inputSeconds;
    const effectiveMaxSeconds =
      maxSeconds !== undefined ? maxSeconds : 999 * 60 + 59;
    const clampedTotalSeconds = clamp(
      totalInputSeconds,
      0,
      effectiveMaxSeconds,
    );
    const finalMinutes = Math.floor(clampedTotalSeconds / 60);
    const finalSeconds = clampedTotalSeconds % 60;

    const formattedMinutes = formatMinutes(finalMinutes);
    const formattedSeconds = formatSeconds(finalSeconds);

    setMinutes(formattedMinutes);
    setSeconds(formattedSeconds);

    if (isInteractive && onChange) {
      const propMinutesFormatted = formatMinutes(displayMinutes);
      const propSecondsFormatted = formatSeconds(displaySeconds);
      if (
        formattedMinutes !== propMinutesFormatted ||
        formattedSeconds !== propSecondsFormatted
      ) {
        onChange(finalMinutes, finalSeconds);
      }
    }

    setEditing('none');
  };

  const notifyChange = (nextMinutes: number, nextSeconds: number) => {
    if (!onChange) {
      return;
    }

    const currentMinutes = Number.parseInt(formatMinutes(displayMinutes), 10);
    const currentSeconds = Number.parseInt(formatSeconds(displaySeconds), 10);

    if (nextMinutes === currentMinutes && nextSeconds === currentSeconds) {
      return;
    }

    onChange(nextMinutes, nextSeconds);
  };

  const handleMinutesChange = (text: string) => {
    if (!isInteractive) return;
    const numericText = text.replace(/[^0-9]/g, '').slice(0, 3);
    setMinutes(numericText);

    notifyChange(
      Number.parseInt(numericText, 10) || 0,
      Number.parseInt(seconds, 10) || 0,
    );
  };

  const handleSecondsChange = (text: string) => {
    if (!isInteractive) return;
    const numericText = text.replace(/[^0-9]/g, '').slice(0, 2);
    setSeconds(numericText);

    if (numericText.length === 2) {
      const parsedSeconds = Number.parseInt(numericText, 10) || 0;
      if (parsedSeconds > 59) {
        setSeconds(formatSeconds(59));
      }
    }

    notifyChange(
      Number.parseInt(minutes, 10) || 0,
      Number.parseInt(numericText, 10) || 0,
    );
  };

  const minutesValueToShow =
    editing === 'minutes' ? minutes : formatMinutes(displayMinutes);

  const secondsValueToShow =
    editing === 'seconds' ? seconds : formatSeconds(displaySeconds);

  return (
    <Box
      direction="horizontal"
      style={style.container}
      pointerEvents={isDisabled ? 'none' : 'auto'}
    >
      <Box alignItems="flex-end" flex>
        <TextInput
          accessibilityLabel={
            testIDPrefix ? `${testIDPrefix} minutes` : 'Clock minutes'
          }
          isBottomSheet={isBottomSheet}
          editable={!isDisabled}
          ref={minutesInputRef}
          value={minutesValueToShow}
          onChangeText={handleMinutesChange}
          onFocus={() => handleFocus('minutes')}
          onBlur={commitClockInput}
          keyboardType="number-pad"
          enterKeyHint="done"
          maxLength={3}
          selectTextOnFocus={!isDisabled}
          style={[style.input, style.minutes]}
          pointerEvents={isDisabled ? 'none' : 'auto'}
          testID={testIDPrefix ? `${testIDPrefix}Minutes` : undefined}
        />
      </Box>

      <Text style={style.separator} size="3xl">
        :
      </Text>

      <Box flex>
        <TextInput
          accessibilityLabel={
            testIDPrefix ? `${testIDPrefix} seconds` : 'Clock seconds'
          }
          isBottomSheet={isBottomSheet}
          editable={!isDisabled}
          ref={secondsInputRef}
          value={secondsValueToShow}
          onChangeText={handleSecondsChange}
          onFocus={() => handleFocus('seconds')}
          onBlur={commitClockInput}
          keyboardType="number-pad"
          enterKeyHint="done"
          maxLength={2}
          selectTextOnFocus={!isDisabled}
          style={style.input}
          pointerEvents={isDisabled ? 'none' : 'auto'}
          testID={testIDPrefix ? `${testIDPrefix}Seconds` : undefined}
        />
      </Box>
    </Box>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    overflow: 'hidden',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    borderColor: theme.colors.border,
    width: theme.size(32),
    alignItems: 'center',
    justifyContent: 'center',
    variants: {
      disabled: {
        true: {
          opacity: 0.7,
          pointerEvents: 'none',
          _web: {
            cursor: 'not-allowed',
          },
        },
      },
    },
  },
  separator: {
    lineHeight: theme.fontSize['3xl'],
  },
  input: {
    paddingHorizontal: 0,
    paddingVertical: 0,
    fontSize: theme.fontSize['3xl'],
    color: theme.colors.typography,
    borderWidth: 0,
    flex: 1,
    width: '100%',
    variants: {
      disabled: {
        true: {
          _web: {
            pointerEvents: 'none',
            cursor: 'not-allowed',
            userSelect: 'none',
          },
        },
      },
    },
  },
  minutes: {
    textAlign: 'right',
  },
}));
